import { useAppDispatch, useAppSelector } from "app/hooks";
import { useCallback, useMemo } from "react";
import { goToNextStep, goToPreviousStep, setAnswer } from "../slices/genericProfilingSlice";
import { selectAnswers, selectCurrentQuestionAnswered, selectCurrentQuestionGroup, selectCurrentStep, selectCurrentStepProgress, selectEligibilityResult, selectIsFirstStep, selectIsLastStep, selectVisibleSteps } from "../slices/genericSelectors";
import { PossibleAnswer } from "../types/interfaces";

export const useGenericProfiling = (onSubmit?: (answers: Record<string, PossibleAnswer>) => void) => {
  const dispatch = useAppDispatch();

  const progress = useAppSelector(selectCurrentStepProgress);
  const answers = useAppSelector(selectAnswers);
  const currentStep = useAppSelector(selectCurrentStep);
  const visibleSteps = useAppSelector(selectVisibleSteps);
  const currentQuestionGroup = useAppSelector(selectCurrentQuestionGroup);
  const isFirstStep = useAppSelector(selectIsFirstStep);
  const isLastStep = useAppSelector(selectIsLastStep);
  const isCurrentQuestionAnswered = useAppSelector(selectCurrentQuestionAnswered);
  const eligibilityResult = useAppSelector(selectEligibilityResult);

  const questionsToShow = useMemo(() => {
    const questions = currentQuestionGroup?.questions || [];
    return questions;
  }, [currentQuestionGroup]);

  const handleQuestionAnswer = useCallback((questionKey: string, answer: PossibleAnswer[]) => {
    dispatch(setAnswer({ questionKey, value: answer }));
  }, [dispatch]);

  const handleNext = useCallback(() => {
    if (isLastStep && isCurrentQuestionAnswered) {
      const formattedAnswers: Record<string, any> = {};
      Object.entries(answers).forEach(([key, values]) => {
        formattedAnswers[key] = values && values.length > 0 ? values : null;
      });
      onSubmit && onSubmit(formattedAnswers);
    } else if (isCurrentQuestionAnswered) {
      dispatch(goToNextStep());
    } else {
    }
  }, [dispatch, isLastStep, isCurrentQuestionAnswered, answers, onSubmit]);

  const handlePrevious = useCallback(() => {
    dispatch(goToPreviousStep());
  }, [dispatch]);

  const currentStepInfo = useMemo(() => {
    const stepIndex = visibleSteps.findIndex(s => s.step === currentStep);
    const info = {
      current: stepIndex + 1,
      total: visibleSteps.length,
      percentage: visibleSteps.length > 0 ? ((stepIndex + 1) / visibleSteps.length) * 100 : 0
    };
    return info;
  }, [visibleSteps, currentStep]);

  return {
    // État
    answers,
    currentStep,
    visibleSteps,
    currentQuestionGroup,
    questionsToShow,
    eligibilityResult,

    // Flags
    isFirstStep,
    isLastStep,
    isCurrentQuestionAnswered,

    // Actions
    handleQuestionAnswer,
    handleNext,
    handlePrevious,

    // Infos
    currentStepInfo,
    progress
  };
};
